import { memo } from "react";
import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import { PresenceStack } from "@components/avatar/PresenceStack";
import { UserAvatar } from "@components/avatar/UserAvatar";
import type { ClientsWorkOverviewWorkRow } from "@components/organizationView/clients-work/clientsWorkOverviewViewConfig";
import {
    getWorkCardContextField,
    getWorkCardLabel,
    getWorkCardTitle,
    WorkCardEntityAvatar,
} from "@components/organizationView/clients-work/board/WorkBoardCard";
import type { WorkCardFieldColumn } from "@components/organizationView/clients-work/board/workCardFieldColumns";
import { getWorkCardTagFields } from "@components/organizationView/clients-work/board/workCardFields";
import { getWorkCardPresenceMembers } from "@components/organizationView/clients-work/board/workCardPresence";
import { getWorkCardFieldName } from "@components/organizationView/clients-work/board/WorkCardTag";
import {
    type ClientsWorkOverviewCallbacks,
    WorkRowActionItems,
} from "@components/organizationView/clients-work/WorkRowActions";
import { BoardCard } from "@design-system/Board/BoardCard";
import { BoardCardAvatarField } from "@design-system/Board/BoardCardAvatarField";
import { BoardCardLayout } from "@design-system/Board/BoardCardLayout";
import { BoardCardMenu } from "@design-system/Board/BoardCardMenu";
import { Menu } from "@design-system/Menu/Menu.lib";
import type { AgentDrawerMode } from "./boardPlaygroundSettings";
import { getDemoViewers, toDemoUser } from "./demoPresence";
import { DEMO_PEOPLE } from "./fixtures/demoTasks";
import { TaskBoardActivity } from "./TaskBoardActivity";

interface PlaygroundWorkCardProps {
    row: ClientsWorkOverviewWorkRow;
    fieldColumns: readonly WorkCardFieldColumn[];
    /** The drawer while the application's agent runs. */
    agentDrawer: AgentDrawerMode;
}

const DEMO_USERS = DEMO_PEOPLE.map(toDemoUser);

/** Applied by as the real tag shows it, for a demo person: the real one reads the organization's members. */
const DemoAppliedBy = ({ userId }: { userId: number }) => {
    const user = DEMO_USERS.find(({ id }) => id === userId);
    return (
        user && (
            <BoardCardAvatarField
                field={getWorkCardFieldName(
                    ClientsWorkOverviewColumn.APPLIED_BY,
                )}
                value={user.name}
            >
                <UserAvatar user={user} size="sm" />
            </BoardCardAvatarField>
        )
    );
};

const STUB_CALLBACKS: ClientsWorkOverviewCallbacks = {
    onOpenOccurrencesDrawer: () => undefined,
    onGoToTemplate: () => undefined,
};

const PlaygroundWorkCardImpl = ({
    row,
    fieldColumns,
    agentDrawer,
}: Readonly<PlaygroundWorkCardProps>) => {
    const isAgentWorking = agentDrawer !== "none" && row.isAgentRunning;
    const presenceMembers = getWorkCardPresenceMembers({
        row,
        isAgentWorking,
        viewers: getDemoViewers(row.id),
    });
    const menuItems = (
        <>
            <Menu.Item onClick={() => undefined}>Open</Menu.Item>
            <WorkRowActionItems
                row={row}
                callbacks={STUB_CALLBACKS}
                onConfirmationRequest={() => undefined}
            />
        </>
    );
    const tagFields = getWorkCardTagFields({
        row,
        fieldColumns,
        userId: undefined,
    }).map((field) =>
        field.id === ClientsWorkOverviewColumn.APPLIED_BY &&
        row.createdByUserId !== null
            ? {
                  ...field,
                  content: <DemoAppliedBy userId={row.createdByUserId} />,
              }
            : field,
    );
    return (
        <Menu.Context
            trigger={
                <BoardCard
                    onClick={() => undefined}
                    aria-label={getWorkCardLabel({ row, isClientShown: true })}
                    drawer={
                        isAgentWorking && (
                            <TaskBoardActivity mode={agentDrawer} />
                        )
                    }
                >
                    <BoardCardLayout
                        leading={<WorkCardEntityAvatar row={row} />}
                        title={getWorkCardTitle(row)}
                        menu={
                            <BoardCardMenu aria-label="Work actions">
                                {menuItems}
                            </BoardCardMenu>
                        }
                        fields={[
                            getWorkCardContextField({
                                row,
                                isClientShown: true,
                            }),
                            ...tagFields,
                            {
                                id: "presence",
                                placement: "bottomEnd",
                                content: presenceMembers.length > 0 && (
                                    <PresenceStack members={presenceMembers} />
                                ),
                            },
                        ]}
                    />
                </BoardCard>
            }
        >
            {menuItems}
        </Menu.Context>
    );
};

/**
 * A Work card for the board playground, built from the real Work card's
 * pieces: the entity as a plain title, the `Client · Service` context line,
 * List's columns as read-only tags (stage first), the real menu and the
 * agent, viewers and engagement team bottom right. Clicks and the menu are
 * stubs.
 */
export const PlaygroundWorkCard = memo(PlaygroundWorkCardImpl);
